import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Panel, Empty } from "@/components/app/PageParts";
import { PropertySelect, PropertyTag } from "@/components/app/PropertySelect";
import { AnswerSheetBadge, KindBadge, ManualGuidanceNote, VersionStatus } from "@/components/app/FormBits";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";

const MATCH = {
  likely_relevant: ["May apply", "bg-emerald-50 text-emerald-800"],
  need_facts: ["We need a few facts", "bg-amber-50 text-amber-900"],
  not_for_location: ["Not for this location", "bg-slate-100 text-slate-600"],
};

const FormCard = ({ form, base, children }) => {
  const cur = form.versions.find((v) => v.id === form.current);
  return (
    <div data-testid={`form-card-${form.id}`} className="rounded-xl border bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-2"><KindBadge kind={form.kind} /><VersionStatus v={cur} />{!cur.fillable && <AnswerSheetBadge testId={`form-card-answer-sheet-${form.id}`} />}</div>
      <Link to={`${base}/forms/${form.id}`} data-testid="form-card-open-link" className="mt-3 block font-display text-lg font-semibold text-navy hover:underline">{form.number}: {form.title}</Link>
      <p className="text-xs text-slate-500">{form.agency} · {form.area} · {cur.revision}</p>
      {!cur.fillable && <p className="mt-2 text-xs text-amber-900" data-testid={`form-card-answer-sheet-note-${form.id}`}>EVR won't fill this form. You'll get an answer sheet to copy onto the {form.kind === "official" ? "official form" : "record"} yourself.</p>}
      {children}
    </div>
  );
};

const Finder = ({ catalog, base, workspace, properties }) => {
  const [goal, setGoal] = useState("");
  const [pid, setPid] = useState(properties[0]?.id || null);
  const [res, setRes] = useState(null);
  const find = async () => {
    try { const { data } = await api.post(`/workspaces/${workspace.id}/forms/find`, { goal, property_id: pid }); setRes(data); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="space-y-6">
      <div className="grid gap-4 rounded-xl border bg-white p-5 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div className="space-y-2"><p className="text-sm font-medium text-navy">What are you trying to do?</p>
          <Select value={goal} onValueChange={setGoal}><SelectTrigger data-testid="finder-goal-select" className="h-11"><SelectValue placeholder="Choose a goal" /></SelectTrigger>
            <SelectContent>{catalog.goals.map((g) => <SelectItem key={g.id} value={g.id} data-testid={`finder-goal-${g.id}`}>{g.label}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-2"><p className="text-sm font-medium text-navy">Which property?</p>
          <PropertySelect properties={properties} value={pid} onChange={setPid} testId="finder-property-select" /></div>
        <Button onClick={find} disabled={!goal} data-testid="finder-submit-button" className="h-11 bg-navy hover:bg-navy-2">Show forms</Button>
      </div>
      {res && (
        <div className="space-y-4" data-testid="finder-results">
          <ManualGuidanceNote />
          {res.results.map((r) => (
            <FormCard key={r.form.id} form={r.form} base={base}>
              <span data-testid={`finder-match-${r.match}`} className={`mt-3 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${MATCH[r.match][1]}`}>{MATCH[r.match][0]}</span>
              <p className="mt-2 text-sm text-slate-700">{r.location_note}</p>
              <p className="mt-2 text-sm text-slate-700"><strong className="text-navy">Why it may apply: </strong>{r.form.why}</p>
              {r.form.info_url && <a href={r.form.info_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-navy underline" data-testid="finder-source-link">Official source <ExternalLink className="h-3 w-3" /></a>}
              {r.missing_facts.length > 0 && <ul className="mt-3 space-y-1 text-sm text-amber-900" data-testid="finder-missing-facts">{r.missing_facts.map((q) => <li key={q}>• {q}</li>)}</ul>}
              <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Questions to check before using it</p>
              <ul className="mt-1 space-y-1 text-sm text-slate-600">{r.form.facts_to_check.map((q) => <li key={q}>• {q}</li>)}</ul>
              {r.role_note && <p className="mt-2 text-xs text-slate-500">{r.role_note}</p>}
            </FormCard>
          ))}
        </div>
      )}
    </div>
  );
};

const Library = ({ catalog, base }) => {
  const [role, setRole] = useState("all");
  const forms = catalog.forms.filter((f) => role === "all" || f.roles.includes(role));
  const groups = [...new Set(forms.map((f) => f.purpose_label))];
  return (
    <div className="space-y-6">
      <div className="w-full sm:w-64"><Select value={role} onValueChange={setRole}><SelectTrigger data-testid="library-role-filter" className="h-10 bg-white"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">For everyone</SelectItem><SelectItem value="homeowner">For homeowners</SelectItem><SelectItem value="agent">For real estate agents</SelectItem></SelectContent></Select></div>
      {groups.map((g) => (
        <section key={g}><h2 className="mb-3 font-display text-base font-semibold text-navy">{g}</h2>
          <div className="grid gap-4 md:grid-cols-2">{forms.filter((f) => f.purpose_label === g).map((f) => (
            <FormCard key={f.id} form={f} base={base}>
              {f.versions.filter((v) => !v.usable).map((v) => <p key={v.id} className="mt-2 text-xs text-amber-800" data-testid="library-outdated-version">{v.revision}: {v.note}</p>)}
            </FormCard>))}</div>
        </section>
      ))}
    </div>
  );
};

const Drafts = ({ workspace, base, properties }) => {
  const [drafts, setDrafts] = useState(null);
  const load = useCallback(() => { api.get(`/workspaces/${workspace.id}/drafts`).then(({ data }) => setDrafts(data.drafts)).catch((e) => toast.error(errorMessage(e))); }, [workspace.id]);
  useEffect(load, [load]);
  const remove = async (d) => { try { await api.delete(`/workspaces/${workspace.id}/drafts/${d.id}`); load(); } catch (e) { toast.error(errorMessage(e)); } };
  return (
    <Panel title="My drafts" testId="drafts-list">
      {drafts && !drafts.length ? <Empty testId="drafts-empty">No drafts yet. Open a form and start one.</Empty> : (
        <ul className="divide-y">{(drafts || []).map((d) => (
          <li key={d.id} data-testid={`draft-row-${d.id}`} className="flex items-center gap-4 px-5 py-4">
            <div className="min-w-0 flex-1">
              <Link to={`${base}/forms/drafts/${d.id}`} className="text-sm font-medium text-navy hover:underline" data-testid="draft-open-link">{d.title}</Link>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span>{d.form_number} · {d.revision} · Updated {formatDate(d.updated_at)}</span><PropertyTag properties={properties} id={d.property_id} />
                {!d.version_status.usable && <span className="text-amber-800" data-testid="draft-paused-badge">Form paused</span>}
                <span>{d.status === "exported" ? "Exported" : `${d.check_issues} open item(s)`}</span>
              </div>
            </div>
            <button onClick={() => remove(d)} data-testid="draft-delete-button" aria-label="Delete draft" className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
          </li>))}</ul>
      )}
    </Panel>
  );
};

export default function Forms() {
  const { workspace, properties } = useOutletContext();
  const [catalog, setCatalog] = useState(null);
  const base = `/app/w/${workspace.id}`;
  useEffect(() => { api.get("/forms/catalog").then(({ data }) => setCatalog(data)).catch((e) => toast.error(errorMessage(e))); }, []);
  return (
    <div data-testid="forms-page">
      <PageHeader eyebrow="Forms & help" title="Forms" subtitle="Find official forms that may fit your situation, understand each question, and prepare your own answers privately." />
      {catalog && (
        <Tabs defaultValue="find">
          <TabsList className="mb-6"><TabsTrigger value="find" data-testid="forms-tab-find">Find a form</TabsTrigger><TabsTrigger value="library" data-testid="forms-tab-library">Library</TabsTrigger><TabsTrigger value="drafts" data-testid="forms-tab-drafts">My drafts</TabsTrigger></TabsList>
          <TabsContent value="find"><Finder catalog={catalog} base={base} workspace={workspace} properties={properties} /></TabsContent>
          <TabsContent value="library"><Library catalog={catalog} base={base} /></TabsContent>
          <TabsContent value="drafts"><Drafts workspace={workspace} base={base} properties={properties} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
